<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import CannedResponseAPI from 'dashboard/api/cannedResponse';
import OnboardingAPI from 'dashboard/api/onboarding';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

const props = defineProps({
  progress: { type: Object, required: true },
});
const emit = defineEmits(['refresh', 'update:can-advance']);

const { t } = useI18n();

// Same minLength(2) rule as AddCanned.vue's Vuelidate validation.
const SHORT_CODE_MIN_LENGTH = 2;

const isStepAlreadyCompleted = computed(() =>
  props.progress.completed_steps.includes(7)
);

// The same row list doubles as the display for already-created canned
// responses (status "success", carrying the real id) and the inputs for new
// ones — no separate list, same pattern as Step02Agents.
const emptyRow = () => ({
  id: null,
  short_code: '',
  content: '',
  status: null,
  error: null,
});

const responseRow = response => ({
  id: response.id,
  short_code: response.short_code,
  content: response.content,
  status: 'success',
  error: null,
});

const rows = ref([emptyRow()]);

const isRowValid = row =>
  row.short_code.trim().length >= SHORT_CODE_MIN_LENGTH &&
  row.content.trim().length > 0;

const addRow = () => {
  rows.value.push(emptyRow());
};

const markStepCompleted = async () => {
  try {
    await OnboardingAPI.update({
      completed_steps: [...new Set([...props.progress.completed_steps, 7])],
    });
    emit('refresh');
  } catch (error) {
    useAlert(t('ONBOARDING.SAVE_ERROR'));
  }
};

const createRow = async index => {
  const row = rows.value[index];
  if (!isRowValid(row) || row.id || row.status === 'saving') return;

  const isFirstResponse = !rows.value.some(r => r.status === 'success');
  row.status = 'saving';
  try {
    const { data } = await CannedResponseAPI.create({
      short_code: row.short_code,
      content: row.content,
    });
    row.id = data.id;
    row.status = 'success';
    row.error = null;
    emit('update:can-advance', true);
    if (isFirstResponse) await markStepCompleted();
  } catch (error) {
    row.status = 'error';
    row.error =
      error?.response?.data?.error || t('ONBOARDING.STEP_7.CREATE_ERROR');
  }
};

const updateRow = async index => {
  const row = rows.value[index];
  if (row.status === 'saving') return;

  row.status = 'saving';
  try {
    await CannedResponseAPI.update(row.id, {
      short_code: row.short_code,
      content: row.content,
    });
    row.status = 'success';
    row.error = null;
  } catch (error) {
    row.status = 'error';
    row.error =
      error?.response?.data?.error || t('ONBOARDING.STEP_7.UPDATE_ERROR');
  }
};

// Rows are saved as soon as they become valid (on blur) — there's no separate
// "Save" button in the design, just short code/content columns and a delete action.
const attemptSave = index => {
  const row = rows.value[index];
  if (!isRowValid(row)) return;
  if (row.id) {
    updateRow(index);
  } else {
    createRow(index);
  }
};

const removeRow = async index => {
  const row = rows.value[index];
  if (row.id) {
    try {
      await CannedResponseAPI.delete(row.id);
    } catch (error) {
      useAlert(t('ONBOARDING.SAVE_ERROR'));
      return;
    }
  }
  rows.value.splice(index, 1);
  if (rows.value.length === 0) addRow();
};

// Re-syncs the list when a canned response is added/edited/removed from
// another screen (e.g. Settings > Canned Responses) so both views stay live.
// Draft rows the user hasn't saved yet are left alone.
const refreshFromServer = async () => {
  try {
    const { data } = await CannedResponseAPI.get({});
    const draftRows = rows.value.filter(row => !row.id);
    const syncedRows = (data || []).map(responseRow);
    rows.value =
      syncedRows.length || draftRows.length
        ? [...syncedRows, ...draftRows]
        : [emptyRow()];
    emit(
      'update:can-advance',
      syncedRows.length > 0 || draftRows.some(isRowValid)
    );
  } catch (error) {
    // Ignore — a background refresh failing shouldn't disrupt an in-progress edit
  }
};

onMounted(async () => {
  try {
    const { data } = await CannedResponseAPI.get({});
    if (data?.length) rows.value = data.map(responseRow);
    emit('update:can-advance', data?.length > 0);
    if (data?.length && !isStepAlreadyCompleted.value)
      await markStepCompleted();
  } catch (error) {
    emit('update:can-advance', false);
    useAlert(t('ONBOARDING.SAVE_ERROR'));
  }
  emitter.on(BUS_EVENTS.CANNED_RESPONSES_CHANGED, refreshFromServer);
});

onUnmounted(() => {
  emitter.off(BUS_EVENTS.CANNED_RESPONSES_CHANGED, refreshFromServer);
});
</script>

<template>
  <div class="flex flex-col items-center justify-start w-full p-8 md:px-16">
    <div class="flex flex-col items-start w-full max-w-2xl gap-4 text-left">
      <div class="flex items-center flex-wrap gap-3">
        <h1 class="text-2xl font-semibold text-n-slate-12">
          {{ t('ONBOARDING.STEP_7.TITLE') }}
        </h1>
        <a
          href="https://help.leadster.com.br/article/419-respostas-prontas-agilizando-o-que-a-equipe-digita-todo-dia"
          target="_blank"
          rel="noopener noreferrer"
          class="flex items-center gap-1 px-3 py-1 text-xs rounded-full shrink-0 bg-n-brand/10 text-n-brand hover:brightness-110"
        >
          <i class="i-lucide-circle-help text-sm" />
          {{ t('ONBOARDING.STEP_7.TUTORIAL_LINK') }}
        </a>
      </div>
      <p class="text-n-slate-12">
        {{ t('ONBOARDING.STEP_7.SUBTITLE') }}
        <span class="font-normal text-n-slate-10">{{
          t('ONBOARDING.REQUIRED_BADGE')
        }}</span>
      </p>
      <p class="text-n-slate-11">{{ t('ONBOARDING.STEP_7.DESCRIPTION') }}</p>

      <div class="flex flex-col items-start w-full max-w-2xl gap-3">
        <div
          v-for="(row, index) in rows"
          :key="index"
          class="flex items-start w-full gap-2 p-3 border rounded-lg border-n-weak"
        >
          <div class="flex flex-col flex-1 gap-2">
            <Input
              v-model="row.short_code"
              custom-input-class="no-margin"
              :label="t('ONBOARDING.STEP_7.SHORT_CODE_LABEL')"
              :placeholder="t('ONBOARDING.STEP_7.SHORT_CODE_PLACEHOLDER')"
              @blur="attemptSave(index)"
            />
            <TextArea
              v-model="row.content"
              auto-height
              :label="t('ONBOARDING.STEP_7.CONTENT_LABEL')"
              :placeholder="t('ONBOARDING.STEP_7.CONTENT_PLACEHOLDER')"
              @blur="attemptSave(index)"
            />
            <p v-if="row.status === 'error'" class="text-sm text-n-ruby-9">
              {{ row.error }}
            </p>
          </div>
          <Button
            icon="i-lucide-trash"
            variant="faded"
            color="ruby"
            type="button"
            @click="removeRow(index)"
          />
        </div>

        <Button
          type="button"
          variant="faded"
          icon="i-lucide-plus"
          :label="t('ONBOARDING.STEP_7.ADD_ROW')"
          @click="addRow"
        />
      </div>
    </div>
  </div>
</template>
